/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAtom,
  useStateXAtomValue,
  useStateXRemover,
} from '@cloudio-saas/statex';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle as MuiDialogTitle,
  IconButton,
  Paper,
  PaperProps,
  Theme,
  Typography,
  SxProps,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { DomProps, Platform } from 'componentregistry';
import zIndex from 'constants/ZIndex';
import useComponentSize from 'hooks/useComponentSize';
import useMove from 'hooks/useMove';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import ResizeHandler from 'layout/Sidebar/ResizeHandler';
import ErrorBoundary from 'platform/ErrorBoundary';
import { usePlatform } from 'platform/PlatformHooks';
import React, {
  forwardRef,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { usePanelWidth } from 'root/Float';
import SimpleLoading from 'root/SimpleLoading';
import { popupPropsAtom, popupPropsRootAtom } from './state';
import { pageSXAtom, appSXAtom } from 'designer/atoms';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  body: {
    paddingLeft: 20,
    paddingTop: 8,
    overflow: 'auto',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 52,
    top: 80,
  },
  header: {
    boxShadow: 'none',
    color: theme.palette.text.primary,
    cursor: 'move',
  },

  paddingZero: {
    padding: 0,
  },
  icon: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
}));

const useActionStyles = makeStyles((theme: Theme) => ({
  actionRoot: {
    margin: 0,
    padding: 0,
  },
}));

const useDialogPopupStyles = makeStyles((theme: Theme) => ({
  padding12: {
    padding: 12,
  },
  padding0: {
    padding: 0,
  },
  body: {
    overflow: 'hidden',
    position: 'relative',
  },
}));

export interface DialogTitleProps {
  children: React.ReactNode;
  onClose?: () => void;
  toolbar?: React.ReactNode;
  itemId: string;
}

const DialogTitle = forwardRef(
  (props: DialogTitleProps, ref: React.Ref<HTMLDivElement>) => {
    const classes = useStyles();
    const { children, onClose, toolbar, itemId, ...other } = props;
    return (
      <MuiDialogTitle
        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
        ref={ref}
        {...other}
        classes={{ root: classes.header }}
      >
        <Typography
          whiteSpace="nowrap"
          sx={{ marginRight: 2 }}
          variant="h6"
          component="span"
        >
          {children}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {toolbar}
        {onClose ? (
          <IconButton
            aria-label="close"
            data-testid={`close-popup-${itemId}`}
            className={classes.closeButton}
            onClick={onClose}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Close Popup
            </span>
            <Icon iconName="times" />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  },
);

const DialogActions = forwardRef(
  (
    { children }: { children: React.ReactNode },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const classes = useActionStyles();
    return (
      <div ref={ref} className={classes.actionRoot}>
        {children}
      </div>
    );
  },
);

type DialogPopupProps = {
  Header?: ReactNode;
  appUid: string;
  bodyChildren: ReactElement<{ width: number; height: number }>;
  bodyPadding?: boolean;
  className?: string;
  devMode?: boolean;
  domProps?: DomProps;
  dataTestId?: string;
  fullScreen?: boolean;
  hideHeader?: boolean;
  itemId: string;
  left?: number;
  metadata?: boolean;
  modal: boolean;
  onClose: () => void;
  open: boolean;
  pageId: string;
  platform: Platform;
  popupActions?: ReactNode;
  popupHeight: number;
  popupWidth: number;
  popupref?: React.Ref<HTMLDivElement>;
  resizable?: boolean;
  styles?: React.CSSProperties;
  toolbar?: ReactNode;
  top?: number;
  hideBackdrop?: boolean;
  disableEnforceFocus?: boolean;
  updateDesignerProps?: boolean;
  isLovPopup?: boolean;
};

function makePaperComponent(
  appUid: string,
  pageId: string,
  itemId: string,
  resizable: boolean,
  updateDesignerProps: boolean,
) {
  return function PaperComponent(props: PaperProps) {
    const platform = usePlatform();
    const [ww, wh] = useWindowSize();

    const [width, setWidth] = useStateXAtom(popupPropsAtom, {
      params: { prop: 'popupWidth', pageId, itemId },
    });
    const [height, setHeight] = useStateXAtom(popupPropsAtom, {
      params: { prop: 'popupHeight', pageId, itemId },
    });

    const [left, setLeft] = useStateXAtom(popupPropsAtom, {
      params: { prop: 'left', pageId, itemId },
    });
    const [top, setTop] = useStateXAtom(popupPropsAtom, {
      params: { prop: 'top', pageId, itemId },
    });

    const first = useRef(true);
    useEffect(() => {
      if (width && height && first.current) {
        setLeft((ww - width) / 2);
        setTop((wh - height) / 2);
        first.current = false;
      }
    }, [height, setLeft, setTop, wh, width, ww]);

    if (!(width && height)) {
      return null;
    }

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `translateX(${left}px) translateY(${top}px) translateZ(0px)`,
        }}
      >
        {resizable && (
          <>
            <ResizeHandler
              min={200}
              max={ww - 16}
              side="l"
              size={width}
              onResizeStop={() => {
                if (pageId && itemId) {
                  if (updateDesignerProps) {
                    platform.setDesignProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupWidth',
                      width,
                    );
                  } else {
                    platform.setRuntimeProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupWidth',
                      width,
                    );
                  }
                }
              }}
              onMove={(w) => {
                const _left = left - (w - width);
                if (_left >= 0) {
                  setLeft(_left);
                  setWidth(w);
                }
              }}
            />
            <ResizeHandler
              min={200}
              max={ww - 16}
              side="r"
              size={width}
              onResizeStop={() => {
                if (pageId && itemId) {
                  if (updateDesignerProps) {
                    platform.setDesignProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupWidth',
                      width,
                    );
                  } else {
                    platform.setRuntimeProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupWidth',
                      width,
                    );
                  }
                }
              }}
              onMove={(w) => {
                if (left + w <= ww) {
                  setWidth(w);
                }
              }}
            />
            <ResizeHandler
              min={160}
              max={wh - 16}
              side="t"
              size={height}
              onResizeStop={() => {
                if (pageId && itemId) {
                  if (updateDesignerProps) {
                    platform.setDesignProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupHeight',
                      height,
                    );
                  } else {
                    platform.setRuntimeProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupHeight',
                      height,
                    );
                  }
                }
              }}
              onMove={(h) => {
                const _top = top - (h - height);
                if (_top >= 0) {
                  setTop(_top);
                  setHeight(h);
                }
              }}
            />
            <ResizeHandler
              min={160}
              max={wh - 16}
              side="b"
              size={height}
              onResizeStop={() => {
                if (pageId && itemId) {
                  if (updateDesignerProps) {
                    platform.setDesignProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupHeight',
                      height,
                    );
                  } else {
                    platform.setRuntimeProp(
                      appUid,
                      pageId,
                      itemId,
                      'popupHeight',
                      height,
                    );
                  }
                }
              }}
              onMove={(h) => {
                if (top + h < wh) {
                  setHeight(h);
                }
              }}
            />
          </>
        )}
        <Paper
          {...props}
          style={{
            width,
            height,
            maxWidth: 'unset',
            maxHeight: 'unset',
            scale: 1,
            margin: 0,
          }}
        />
      </div>
    );
  };
}

export const DialogPopup = ({
  Header,
  appUid,
  bodyChildren,
  bodyPadding = true,
  className,
  devMode = false,
  domProps,
  dataTestId,
  fullScreen,
  hideHeader,
  itemId,
  metadata,
  modal,
  onClose,
  open,
  pageId,
  platform,
  popupActions,
  popupHeight,
  popupWidth,
  popupref,
  resizable = true,
  styles,
  toolbar,
  hideBackdrop,
  disableEnforceFocus,
  updateDesignerProps = false,
  isLovPopup = false,
}: DialogPopupProps): React.ReactElement => {
  if (!appUid) {
    throw new Error('Missing appUid in DialogPopup!');
  }
  const actionsSize = useComponentSize();
  const headerSize = useComponentSize();
  const classes = useDialogPopupStyles();
  const remove = useStateXRemover();
  const [ww, wh] = useWindowSize();

  useEffect(() => {
    return () => {
      if (process.env.NODE_ENV !== 'test') {
        // cleanup
        remove(popupPropsRootAtom, {
          params: { pageId, itemId },
        });
      }
    };
  }, [itemId, pageId, remove]);

  const [popupLeft, setLeft] = useStateXAtom(popupPropsAtom, {
    params: { prop: 'left', pageId, itemId },
  });

  const [width, setWidth] = useStateXAtom(popupPropsAtom, {
    params: { prop: 'popupWidth', pageId, itemId },
  });
  const [height, setHeight] = useStateXAtom(popupPropsAtom, {
    params: { prop: 'popupHeight', pageId, itemId },
  });
  const [popupTop, setTop] = useStateXAtom(popupPropsAtom, {
    params: { prop: 'top', pageId, itemId },
  });

  const originRef = useRef<{ left: number; top: number } | undefined>(
    undefined,
  );
  const moveProps = useMove('drag', ({ status, offset }) => {
    if (status === 'start') {
      originRef.current = { left: popupLeft, top: popupTop };
    } else if (status === 'end') {
      originRef.current = undefined;
    } else if (originRef.current) {
      let left = originRef.current.left + offset.x;
      if (left < 0) left = 0;
      if (left > ww - 65) left = ww - 65;
      setLeft(left);
      let top = originRef.current.top + offset.y;
      if (top < 0) top = 0;
      if (top > wh - 65) top = wh - 65;
      setTop(top);
    }
  });

  const PaperComponent = useMemo(
    () =>
      makePaperComponent(
        appUid,
        pageId,
        itemId,
        resizable,
        updateDesignerProps,
      ),
    [appUid, updateDesignerProps, itemId, pageId, resizable],
  );

  const { sidebarRight, floatingSidebars, sidebarWidth, pinnedSidebarWidth } =
    usePanelWidth(appUid);
  const panelWidth = floatingSidebars ? 0 : sidebarWidth + pinnedSidebarWidth;
  const fullDevModeWidth = ww - panelWidth - 40;
  const fullDevModeLeft =
    (sidebarRight ? 0 : 40) + (floatingSidebars ? 0 : sidebarWidth);
  const sxPage = useStateXAtomValue(pageSXAtom, {
    params: { appUid, pageId },
  });
  const sxApp = useStateXAtomValue(appSXAtom, {
    params: { appUid },
  });
  useEffect(() => {
    const w = devMode ? fullDevModeWidth : ww;
    if (fullScreen) {
      setWidth(w - 32);
      setHeight(wh - 32);
    } else if (popupWidth && popupHeight) {
      setWidth(Math.min(popupWidth, w - 32));
      setHeight(Math.min(popupHeight, wh - 32));
    }
  }, [devMode, fullDevModeWidth, fullScreen, popupHeight, popupWidth, wh, ww]);

  const footerHeight = actionsSize.height;
  const headerHeight = headerSize.height;

  return (
    <Box
      className="io-dialog-wrapper"
      // mouse drag on scrollbars (code editor) is not working when stopPropagation is used onMouseMove
      // onMouseMove={stopPropagation}
      onMouseDown={(e) => {
        const div = e.target;
        if (
          div instanceof HTMLDivElement &&
          div.className.includes('dvn-scroller') // canvas grid click
        ) {
          // click on canvas grid inside a popup not working when stopPropagation is used onMouseDown/Up
          return;
        }
        e.stopPropagation();
      }}
      onMouseUp={(e) => {
        const div = e.target;
        if (
          div instanceof HTMLDivElement &&
          div.className.includes('dvn-scroller') // canvas grid click
        ) {
          // click on canvas grid inside a popup not working when stopPropagation is used onMouseDown/Up
          return;
        }
        e.stopPropagation();
      }}
      // moved out of the dialog to allow the dialog to close when clicking on the backdrop
      onClick={(e) => {
        const picker = document.querySelector('.custom-datepicker-dialog');
        if (!picker) {
          e.stopPropagation();
        }
      }}
    >
      <Dialog
        data-testid={dataTestId || 'popup'}
        fullScreen={fullScreen}
        open={open}
        onClose={(event, reason) => {
          if (
            !modal ||
            (reason !== 'backdropClick' && reason !== 'escapeKeyDown')
          ) {
            onClose();
          }
        }}
        className={className}
        PaperComponent={fullScreen ? undefined : PaperComponent}
        disableEscapeKeyDown={modal}
        ref={popupref}
        {...domProps}
        data-metadata={metadata ? 'cloudio' : undefined}
        hideBackdrop={hideBackdrop ?? devMode}
        disableEnforceFocus={disableEnforceFocus ?? devMode}
        style={{
          zIndex: devMode
            ? 100 + 5
            : isLovPopup
            ? zIndex.Model + 10
            : zIndex.Model,
          left: devMode && fullScreen ? fullDevModeLeft : undefined,
          width: devMode && fullScreen ? fullDevModeWidth : undefined,
        }}
        sx={
          [
            { bgcolor: 'transparent !important' },
            sxApp,
            sxPage,
          ] as SxProps<Theme>
        }
        PaperProps={{ className }}
        BackdropProps={{ 'aria-label': 'Menu Backdrop' }}
      >
        {!hideHeader ? (
          <DialogTitle
            ref={headerSize.ref}
            onClose={modal || toolbar ? undefined : onClose}
            {...moveProps}
            toolbar={toolbar}
            itemId={itemId}
          >
            {Header}
          </DialogTitle>
        ) : null}
        <DialogContent
          style={styles}
          className={clsx(
            classes.body,
            bodyPadding ? classes.padding12 : classes.padding0,
          )}
        >
          <ErrorBoundary platform={platform}>
            <React.Suspense fallback={<SimpleLoading name="PopupBody" />}>
              {bodyChildren
                ? React.cloneElement(bodyChildren, {
                    width: width - (bodyPadding ? 24 : 0),
                    height:
                      (height ?? 400) -
                      headerHeight -
                      (bodyPadding ? 24 : 0) -
                      footerHeight,
                  })
                : null}
            </React.Suspense>
          </ErrorBoundary>
        </DialogContent>
        {popupActions ? (
          <DialogActions ref={actionsSize.ref}>{popupActions}</DialogActions>
        ) : null}
      </Dialog>
    </Box>
  );
};
